import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GameState, GameType, Difficulty, Profile, Question, LevelProgress, Achievement } from '../types';
import { db, getProfileWithStats, saveProgress } from '../services/database';
import { generateQuestion, generateRandomMixQuestion } from '../utils/gameLogic';
import { calculateScore } from '../utils/scoring';
import { generateUUID } from '../utils/uuid';

const ALL_GAME_TYPES: GameType[] = ['multiplication', 'division', 'addition', 'subtraction'];

function makeDefaultUnlocked(): Record<GameType, number[]> {
  return { multiplication: [1], division: [1], addition: [1], subtraction: [1] };
}

function progressKey(gameType: GameType, level: number): string {
  return `${gameType}-${level}`;
}

function toGameType(value: unknown): GameType {
  if (ALL_GAME_TYPES.includes(value as GameType)) return value as GameType;
  return 'multiplication';
}

interface GameStore extends GameState {
  selectProfile: (profileId: string) => Promise<void>;
  createProfile: (name: string, avatar: string, color: string) => Promise<void>;
  deleteProfile: (profileId: string) => Promise<void>;
  loadProfiles: () => Promise<void>;
  initializeApp: () => Promise<void>;

  setGameType: (gameType: GameType) => void;
  setDifficulty: (difficulty: Difficulty) => void;

  startLevel: (level: number) => void;
  startRandomMode: () => void;
  answerQuestion: (answer: number) => Promise<void>;
  nextQuestion: () => void;
  pauseGame: () => void;
  resumeGame: () => void;
  quitLevel: () => void;

  toggleSound: () => void;
  resetProgress: () => Promise<void>;
  loadLevelProgress: () => Promise<void>;
}

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      currentProfile: null,
      profiles: [],
      currentLevel: 0,
      currentQuestion: null,
      score: 0,
      lives: 3,
      questionsAnswered: 0,
      correctAnswers: 0,
      currentStreak: 0,
      bestStreak: 0,
      isPaused: false,
      isRandomMode: false,
      soundEnabled: true,
      gameType: 'multiplication' as GameType,
      difficulty: 'medium' as Difficulty,
      levelProgress: new Map(),
      unlockedLevelsPerGameType: makeDefaultUnlocked(),
      achievements: [],
      sessionId: null,
      startTime: 0,
      elapsedTime: 0,

      setGameType: (gameType: GameType) => set({ gameType }),
      setDifficulty: (difficulty: Difficulty) => set({ difficulty }),

      selectProfile: async (profileId: string) => {
        const profileWithStats = await getProfileWithStats(profileId);
        if (profileWithStats) {
          const progress = await db.progress.where('profileId').equals(profileId).toArray();
          const unlockedLevelsPerGameType = makeDefaultUnlocked();
          progress.forEach(p => {
            const gt: GameType = toGameType(p.gameType);
            if (p.level < 10 && !unlockedLevelsPerGameType[gt].includes(p.level + 1)) {
              unlockedLevelsPerGameType[gt].push(p.level + 1);
            }
          });
          for (const gt of ALL_GAME_TYPES) {
            unlockedLevelsPerGameType[gt] = [...new Set(unlockedLevelsPerGameType[gt])].sort((a, b) => a - b);
          }

          set({
            currentProfile: profileWithStats,
            unlockedLevelsPerGameType
          });
          await get().loadLevelProgress();
        }
      },

      createProfile: async (name: string, avatar: string, color: string) => {
        const newProfile: Profile = {
          id: generateUUID(),
          name,
          avatar,
          color,
          createdAt: new Date(),
          totalStars: 0,
          totalScore: 0,
          levelsCompleted: 0,
          bestStreak: 0
        };

        await db.profiles.add(newProfile);
        await get().loadProfiles();
        await get().selectProfile(newProfile.id);
      },

      deleteProfile: async (profileId: string) => {
        await db.profiles.delete(profileId);
        await db.progress.where('profileId').equals(profileId).delete();
        await db.achievements.where('profileId').equals(profileId).delete();
        await get().loadProfiles();

        if (get().currentProfile?.id === profileId) {
          set({ currentProfile: null, unlockedLevelsPerGameType: makeDefaultUnlocked() });
        }
      },

      loadProfiles: async () => {
        const profiles = await db.profiles.toArray();
        const profilesWithStats = await Promise.all(
          profiles.map(p => getProfileWithStats(p.id))
        );
        set({ profiles: profilesWithStats.filter(p => p !== null) as Profile[] });
      },

      initializeApp: async () => {
        await get().loadProfiles();

        const storedState = localStorage.getItem('space-math-quest');
        if (storedState) {
          try {
            const parsed = JSON.parse(storedState);
            const persistedProfileId = parsed?.state?.currentProfile?.id;

            if (persistedProfileId) {
              const profileWithStats = await getProfileWithStats(persistedProfileId);
              if (profileWithStats) {
                const progress = await db.progress.where('profileId').equals(persistedProfileId).toArray();
                const unlockedLevelsPerGameType = makeDefaultUnlocked();
                progress.forEach(p => {
                  const gt: GameType = toGameType(p.gameType);
                  if (p.level < 10) {
                    unlockedLevelsPerGameType[gt].push(p.level + 1);
                  }
                });
                for (const gt of ALL_GAME_TYPES) {
                  unlockedLevelsPerGameType[gt] = [...new Set(unlockedLevelsPerGameType[gt])].sort((a, b) => a - b);
                }

                const progressMap = new Map<string, LevelProgress>();
                progress.forEach(p => {
                  const gt: GameType = toGameType(p.gameType);
                  const key = progressKey(gt, p.level);
                  const existing = progressMap.get(key);
                  if (!existing || p.score > existing.score) {
                    progressMap.set(key, p);
                  }
                });

                set({
                  currentProfile: profileWithStats,
                  unlockedLevelsPerGameType,
                  levelProgress: progressMap
                });
              }
            }
          } catch (error) {
            console.error('Error restoring profile:', error);
          }
        }
      },

      startLevel: (level: number) => {
        const { gameType, difficulty } = get();
        const question = generateQuestion(level, difficulty, gameType);
        set({
          currentLevel: level,
          isRandomMode: false,
          currentQuestion: question,
          score: 0,
          lives: 3,
          questionsAnswered: 0,
          correctAnswers: 0,
          currentStreak: 0,
          bestStreak: 0,
          isPaused: false,
          sessionId: generateUUID(),
          startTime: Date.now(),
          elapsedTime: 0
        });
      },

      startRandomMode: () => {
        const { gameType, difficulty } = get();
        const question = generateRandomMixQuestion(difficulty, gameType);
        set({
          currentLevel: 0,
          isRandomMode: true,
          currentQuestion: question,
          score: 0,
          lives: 3,
          questionsAnswered: 0,
          correctAnswers: 0,
          currentStreak: 0,
          bestStreak: 0,
          isPaused: false,
          sessionId: generateUUID(),
          startTime: Date.now(),
          elapsedTime: 0
        });
      },

      answerQuestion: async (answer: number) => {
        const state = get();
        if (!state.currentQuestion || !state.currentProfile) return;

        const isCorrect = answer === state.currentQuestion.correctAnswer;
        const newStreak = isCorrect ? state.currentStreak + 1 : 0;
        const newBestStreak = Math.max(newStreak, state.bestStreak);
        const newLives = isCorrect ? state.lives : state.lives - 1;
        const newScore = isCorrect ? state.score + 10 + (newStreak * 2) : state.score;

        set({
          questionsAnswered: state.questionsAnswered + 1,
          correctAnswers: isCorrect ? state.correctAnswers + 1 : state.correctAnswers,
          currentStreak: newStreak,
          bestStreak: newBestStreak,
          lives: newLives,
          score: newScore,
          currentQuestion: {
            ...state.currentQuestion,
            userAnswer: answer,
            isCorrect
          }
        });

        if (state.questionsAnswered + 1 >= 10 || newLives === 0) {
          const accuracy = ((state.correctAnswers + (isCorrect ? 1 : 0)) / (state.questionsAnswered + 1)) * 100;
          const time = Math.floor((Date.now() - state.startTime) / 1000);
          const { total, stars } = calculateScore(
            state.correctAnswers + (isCorrect ? 1 : 0),
            state.questionsAnswered + 1,
            time,
            newBestStreak
          );

          if (newLives > 0) {
            if (!state.isRandomMode) {
              await saveProgress(
                state.currentProfile.id,
                state.currentLevel,
                state.gameType,
                state.difficulty,
                stars,
                total,
                accuracy,
                time,
                newBestStreak
              );

              const newUnlocked = { ...get().unlockedLevelsPerGameType };
              const gt = state.gameType;
              if (state.currentLevel < 10 && !newUnlocked[gt].includes(state.currentLevel + 1)) {
                newUnlocked[gt] = [...newUnlocked[gt], state.currentLevel + 1].sort((a, b) => a - b);
                set({ unlockedLevelsPerGameType: newUnlocked });
              }

              // Refresh progress map
              const updatedProgress = await db.progress.where('profileId').equals(state.currentProfile.id).toArray();
              const progressMap = new Map<string, LevelProgress>();
              updatedProgress.forEach(p => {
                const pgt: GameType = toGameType(p.gameType);
                const key = progressKey(pgt, p.level);
                const existing = progressMap.get(key);
                if (!existing || p.score > existing.score) {
                  progressMap.set(key, p);
                }
              });
              set({ levelProgress: progressMap });
            }
          }
        }
      },

      nextQuestion: () => {
        const state = get();
        if (state.questionsAnswered < 10 && state.lives > 0) {
          const question = state.isRandomMode
            ? generateRandomMixQuestion(state.difficulty, state.gameType)
            : generateQuestion(state.currentLevel, state.difficulty, state.gameType);
          set({ currentQuestion: question });
        }
      },

      pauseGame: () => set({ isPaused: true }),
      resumeGame: () => set({ isPaused: false }),
      quitLevel: () => {
        set({
          currentLevel: 0,
          isRandomMode: false,
          currentQuestion: null,
          score: 0,
          lives: 3,
          questionsAnswered: 0,
          correctAnswers: 0,
          currentStreak: 0,
          bestStreak: 0,
          isPaused: false,
          sessionId: null
        });
      },

      toggleSound: () => {
        set(state => ({ soundEnabled: !state.soundEnabled }));
        const { soundEnabled } = get();
        import('../services/webAudioSound').then(({ soundManager }) => {
          soundManager.setEnabled(!soundEnabled);
        });
      },

      resetProgress: async () => {
        const profileId = get().currentProfile?.id;
        if (profileId) {
          await db.progress.where('profileId').equals(profileId).delete();
          await db.achievements.where('profileId').equals(profileId).delete();
          set({
            unlockedLevelsPerGameType: makeDefaultUnlocked(),
            levelProgress: new Map(),
            achievements: []
          });
        }
      },

      loadLevelProgress: async () => {
        const profileId = get().currentProfile?.id;
        if (profileId) {
          const progress = await db.progress.where('profileId').equals(profileId).toArray();
          const progressMap = new Map<string, LevelProgress>();
          progress.forEach(p => {
            const gt: GameType = toGameType(p.gameType);
            const key = progressKey(gt, p.level);
            const existing = progressMap.get(key);
            if (!existing || p.score > existing.score) {
              progressMap.set(key, p);
            }
          });
          set({ levelProgress: progressMap });
        }
      }
    }),
    {
      name: 'space-math-quest',
      partialize: (state) => ({
        currentProfile: state.currentProfile ? { id: state.currentProfile.id } : null,
        soundEnabled: state.soundEnabled,
        gameType: state.gameType,
        difficulty: state.difficulty
      })
    }
  )
);